/**
 * API utility with retry logic for handling intermittent failures
 */

/**
 * Fetch with retry and timeout
 * @param {string} url - URL to fetch
 * @param {object} options - Fetch options
 * @param {number} retries - Number of retries (default: 3)
 * @param {number} timeout - Timeout in milliseconds (default: 30000)
 * @returns {Promise<Response>}
 */
export async function fetchWithRetry(url, options = {}, retries = 3, timeout = 30000) {
  for (let i = 0; i < retries; i++) {
    try {
      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), timeout)
      );

      // Race between fetch and timeout
      const response = await Promise.race([
        fetch(url, options),
        timeoutPromise
      ]);

      // If response is OK, return it
      if (response.ok) {
        return response;
      }

      // If not OK and not last retry, wait and retry
      if (i < retries - 1) {
        const delay = Math.min(1000 * Math.pow(2, i), 5000); // Exponential backoff, max 5s
        console.warn(`Request failed with ${response.status}, retrying in ${delay}ms... (attempt ${i + 1}/${retries})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      // Last retry failed, return response anyway
      return response;
    } catch (error) {
      // Network error or timeout
      if (i < retries - 1) {
        const delay = Math.min(1000 * Math.pow(2, i), 5000); // Exponential backoff, max 5s
        console.warn(`Request failed: ${error.message}, retrying in ${delay}ms... (attempt ${i + 1}/${retries})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      // Last retry failed, throw error
      throw error;
    }
  }
}

/**
 * Fetch JSON with retry
 * @param {string} url - URL to fetch
 * @param {object} options - Fetch options
 * @param {number} retries - Number of retries (default: 3)
 * @returns {Promise<any>}
 */
export async function fetchJsonWithRetry(url, options = {}, retries = 3) {
  const response = await fetchWithRetry(url, options, retries);
  
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  
  return response.json();
}

